import {
  InputNumber, Table, Radio, Form, Input, Button, PopConfirm,
} from '@roo/roo';
import React, { useRef } from 'react';
import { Column } from '@roo/roo/Table';
import { ReviewTemplateType, JudgeSelection } from './reviewTypes';
import './reviewForm.less';

const Field = Form.Field;

interface JudgeResultLeafScore {
  id: number;
  text: string;
}

interface MatrixScore {
  text: string;
  chosen: JudgeSelection;
}

interface JudgeResultLeafMatrix extends JudgeResultLeafScore {
  score: {
    outstanding: MatrixScore;
    excellent: MatrixScore;
    veryGood: MatrixScore;
    good: MatrixScore;
    average: MatrixScore;
  };
}

interface JudgeResultChild<T> {
  text: string;
  subCriterion: JudgeResultChild<T>[] | T[]; // 目前看来同级的subCriterion类型必须相同
}

interface JudgeResultScore {
  context: JudgeResultChild<JudgeResultLeafScore>[];
  score: number;
}

interface JudgeResultMatrix {
  context: JudgeResultChild<JudgeResultLeafMatrix>[];
}

interface ReviewFormProps {
  detail: {
    judgeResult: JudgeResultScore | JudgeResultMatrix;
    reviewTemplateType: ReviewTemplateType;
    reviewTemplateName: string;
    otherJudgeInfo: string;
  };
  disabled: boolean;
  score: number;
  putDetail: (d: ReviewFormProps['detail']) => void;
  onScoreChange: (s: number) => void;
  action: (type: number) => Promise<void>;
}

interface ReviewTableProps extends ReviewFormProps {
  value: ReturnType<typeof formatJudgeResult>;
  handleChange: (v: { score: JudgePlainItem['score']; id: JudgePlainItem['id'] }[]) => void;
}

interface JudgePlainItem {
  id: number;
  count: number[];
  text: string[];
  score?: {
    outstanding: MatrixScore;
    excellent: MatrixScore;
    veryGood: MatrixScore;
    good: MatrixScore;
    average: MatrixScore;
  };
}

const subIsChild = (x: any = []): x is JudgeResultChild<JudgeResultLeafMatrix>[] => (
  x as UnionToIntersection<JudgeResultChild<JudgeResultLeafMatrix>[] | JudgeResultLeafMatrix[]>
).every((one) => 'subCriterion' in one);

const formatJudgeResult = (
  judgeResult: (JudgeResultChild<JudgeResultLeafScore> | JudgeResultChild<JudgeResultLeafMatrix>)[],
  text: string[] = [],
  index: number[] = [],
): JudgePlainItem[] => judgeResult.reduce((memo: JudgePlainItem[] = [], judgeOne, i) => {
  const nextText = [...text, judgeOne.text];
  const nextIndex = [...index, i];
  const subCriterion = judgeOne.subCriterion || [];
  const isChild = subIsChild(subCriterion) && subCriterion.length;
  if (isChild) {
    return [...memo, ...formatJudgeResult(subCriterion, nextText, nextIndex)];
  }
  const items = subCriterion.map((s, si) => ({
    ...s,
    text: [...nextText, s.text],
    index: [...nextIndex, si],
  }));

  return [...memo, ...items];
}, []) as unknown as JudgePlainItem[];

const isEqualArr = (a: any[], b: any[], number = 1) => Array.from({ length: number }).every((_, index) => a[index] === b[index]);

const fullRender = (props, arr, level) => {
  const { record } = props;
  const rowSpan = arr.filter((jg) => isEqualArr(jg.index, record.index, level)).length;
  const firstOne = arr.find((jg) => isEqualArr(jg.index, record.index, level));
  const shouldHaveRowSpan = firstOne.index.join() === record.index.join();
  return (
    shouldHaveRowSpan && (
      <td rowSpan={rowSpan} style={{ whiteSpace: 'pre-wrap' }}>
        {record.text[level - 1]}
      </td>
    )
  );
};

const ReviewTable = (props: ReviewTableProps) => {
  const {
    disabled, detail, putDetail, value: plainJudgeContext, handleChange,
  } = props;
  const { judgeResult, reviewTemplateType } = detail;
  const isMatrixJudge = reviewTemplateType === ReviewTemplateType.MATRIX;
  const context = plainJudgeContext.map((p) => ({ score: p.score, id: p.id }));

  const compose = (jr: JudgeResultChild<JudgeResultLeafMatrix>[], ctx: typeof context = context) => jr.map((level) => {
    const { subCriterion } = level;
    const subIsLeaf = !!(subCriterion as any).find((one) => 'id' in one);
    const nextSub = !subIsLeaf
      ? compose(subCriterion as JudgeResultChild<JudgeResultLeafMatrix>[], ctx)
      : subCriterion.map((s) => {
        const theContextOne = ctx.find((c) => 'id' in s && c.id === s.id);
        return {
          ...s,
          score: theContextOne.score,
        };
      });

    return {
      ...level,
      subCriterion: nextSub,
    };
  });

  const onRadioSelection = (id: number, key: string) => {
    if (reviewTemplateType === ReviewTemplateType.SCORE) {
      return;
    }
    const nextContext = context.map((c) => {
      if (c.id !== id) {
        return c;
      }
      const scoreEntry = Object.entries(c.score).map(([k, value]) => {
        if (k !== key) {
          return [k, { ...value, chosen: JudgeSelection.NO }];
        }

        return [k, { ...value, chosen: JudgeSelection.YES }];
      });

      return {
        ...c,
        score: Object.fromEntries(scoreEntry),
      };
    });

    handleChange(nextContext);

    putDetail({
      ...detail,
      judgeResult: {
        context: compose(
          judgeResult.context as JudgeResultChild<JudgeResultLeafMatrix>[],
          nextContext,
        ),
      },
    });
  };

  const matrixColumns: Column<any>[] = isMatrixJudge
    ? [
      {
        label: '评级',
        align: 'center',
        width: '33.18%',
        children: plainJudgeContext[0]
          ? Object.entries(plainJudgeContext[0].score).map(([key, value]) => {
            const label = value.text;
            return {
              label,
              render: (_, item) => {
                const one = context.find((c) => c.id === item.id);
                const chosen = one.score[key].chosen === JudgeSelection.YES;

                return (
                  <Radio
                    checked={chosen}
                    disabled={disabled}
                    onChange={() => onRadioSelection(item.id, key)}
                  />
                );
              },
            };
          })
          : [],
      },
    ]
    : [];

  const columns: Column<any>[] = [
    {
      label: '一级指标',
      align: 'center',
      width: '8.45%',
      fullRender: (props) => fullRender(props, plainJudgeContext, 1),
    },
    {
      label: '二级指标',
      align: 'center',
      width: '11.72%',
      fullRender: (props) => fullRender(props, plainJudgeContext, 2),
    },
    {
      label: '评价要素',
      align: 'center',
      width: isMatrixJudge ? '46.69%' : '79.83%',
      fullRender: (props) => fullRender(props, plainJudgeContext, 3),
    },
    ...matrixColumns,
  ];

  return (
    <div className="review-form__table">
      <Table columns={columns} data={plainJudgeContext} rowKey="id" border />
    </div>
  );
};

const ReviewForm = (props: ReviewFormProps) => {
  const {
    disabled, score, detail, putDetail, action,
  } = props;
  const {
    judgeResult, reviewTemplateType, reviewTemplateName, otherJudgeInfo,
  } = detail;
  const isMatrixJudge = reviewTemplateType === ReviewTemplateType.MATRIX;
  const plainJudgeContext = formatJudgeResult(judgeResult.context);
  const submitting = useRef(false);

  // roo是不是没办法设置trigger? table的校验最好是提交校验
  const rules = {
    table: {
      validator(rule, value, callback) {
        // 非提交状态不校验，避免选一项就提示没选完
        if (!submitting.current || !isMatrixJudge) {
          callback();
          return;
        }

        // 每一项的score如果没有一个是选中的，那么就表示还没完成
        const unFinished = value.some(
          (one) => !Object.keys(one.score).some((k) => one.score[k].chosen === JudgeSelection.YES),
        );

        callback(unFinished ? '有未评级内容，无法提交' : undefined);
      },
    },

    score: [
      {
        required: !isMatrixJudge,
        type: 'number',
        min: 0,
        max: 100,
        message: '请输入0-100之间的分数',
      },
    ],
    comment: [
      {
        required: false, type: 'string', min: 0, max: 2000, message: '反馈信息在2000字以内',
      },
    ],
  };
  const formValue = {
    score,
    comment: otherJudgeInfo,
    table: plainJudgeContext,
  };

  const onScoreChange = (handleChange, v: number) => {
    const sanitizedValue = (`${v}`).endsWith('.') ? v : parseFloat((+v).toFixed(2));
    handleChange(sanitizedValue);
    putDetail({
      ...detail,
      judgeResult: {
        ...detail.judgeResult,
        score: sanitizedValue,
      },
    });
  };

  const onCommentChange = (handleChange, v: string) => {
    handleChange(v);
    putDetail({
      ...detail,
      otherJudgeInfo: v,
    });
  };

  const submit = async (_, errors) => {
    if (errors && errors.length) {
      submitting.current = false;
      return;
    }

    await action(submitting.current ? 2 : 1);
    submitting.current = false;
  };

  const validate = (rawSubmit, stage) => {
    if (stage === 2) {
      submitting.current = true;
    }

    rawSubmit(stage);
  };
  return (
    <Form rules={rules} value={formValue} layout="vertical" onSubmit={submit}>
      {({ submitForm }) => (
        <form className="review-form">
          <Field>{!disabled && <p className="review-form__head">{reviewTemplateName}</p>}</Field>
          <Field name="table">
            {({ value, handleChange }) => (
              <ReviewTable {...props} value={value} handleChange={handleChange} />
            )}
          </Field>

          {!isMatrixJudge && (
            <Field name="score">
              {({ value, handleChange }) => (
                <div className="review-form__foot">
                  {disabled ? (
                    <p className="sub-title">
                      评审打分
                      {score}
                      分
                    </p>
                  ) : (
                    <>
                      <p className="sub-title">请输入您的评审打分</p>
                      <InputNumber
                        value={value}
                        disabled={disabled}
                        showOperator={false}
                        onChange={onScoreChange.bind(null, handleChange)}
                      />
                      <span className="review-form__foot__percent">百分制</span>
                    </>
                  )}
                </div>
              )}
            </Field>
          )}

          <Field name="comment">
            {({ value, handleChange }) => (disabled ? (
              otherJudgeInfo ? (
                <div className="commit">
                  <p className="sub-title">其他反馈信息</p>
                  <div className="commit__desc">{otherJudgeInfo}</div>
                </div>
              ) : null
            ) : (
              <div>
                <p className="sub-title">其他反馈信息</p>
                <Input.Textarea
                  disabled={disabled}
                  value={value}
                  maxLength={2000}
                  onChange={(e) => onCommentChange(handleChange, e.currentTarget.value)}
                  className="commit"
                ></Input.Textarea>
                <p className="judgeInfo">
                  {otherJudgeInfo.length}
                  /2000
                </p>
              </div>
            ))}
          </Field>

          <Field>
            <div className="review-form__buttons">
              {!disabled && (
                <>
                  <Button disabled={disabled} auto={false} onClick={() => validate(submitForm, 1)}>
                    保存
                  </Button>
                  &nbsp;&nbsp;&nbsp;
                  <PopConfirm
                    disabled={disabled}
                    content={<span>确认提交评审吗？提交之后不可修改</span>}
                    onConfirm={() => validate(submitForm, 2)}
                  >
                    <Button disabled={disabled} auto={false}>
                      提交
                    </Button>
                  </PopConfirm>
                </>
              )}
            </div>
          </Field>
        </form>
      )}
    </Form>
  );
};

export default ReviewForm;
